import Typography from "@/components/typography/Typography"
import { useBreakpoint } from "@/hooks/useBreakpoint"
import AdvantageCard, {
  ADVANTAGE_IMG_MAP,
  AdvantageCardProps,
} from "@/pages/website-landing/home/new-sections/problem-solution-section/advantage-card/AdvantageCard"
import HexListItem, {
  ICONS_MAP,
} from "@/pages/website-landing/home/new-sections/problem-solution-section/hexx-list-item/HexListItem"
import { Col, Row } from "antd"
import { FC } from "react"
import "./ProblemSolutionSection.less"
import afterImg from "./assets/after-img.webp"
import beforeImg from "./assets/before-img.webp"
import connector from "./assets/connector.svg"
import straightConnector from "./assets/straight-connector.svg"
import wideLogo from "./assets/wide-bg-logo.svg"

const hexListItems = [
  {
    icon: ICONS_MAP?.envelope,
    text: "Endless emails",
  },
  {
    icon: ICONS_MAP?.admin,
    text: "Manual admin",
  },
  {
    icon: ICONS_MAP?.memoCircle,
    text: "Missing documents",
  },
  {
    icon: ICONS_MAP?.octagon,
    text: "Delayed approvals",
  },
  {
    icon: ICONS_MAP?.crossCircle,
    text: "Incorrect authority submissions",
  },
]

const advantageCardContent: AdvantageCardProps[] = [
  {
    image: ADVANTAGE_IMG_MAP?.autoWorkflow,
    title: "Automate manual\nworkflows",
    text: "Reduce repetitive admin and keep applications progressing with less effort.",
    mode: "vertical",
  },
  {
    image: ADVANTAGE_IMG_MAP?.network,
    title: "Simplify complex\nprocesses",
    text: "Manage stakeholders, communication, and documentation from one central system.",
    mode: "vertical",
  },
  {
    image: ADVANTAGE_IMG_MAP?.correspondence,
    title: "Manage incoming & outgoing applicationss",
    text: "Handle third-party applications and your own outgoing submissions in one streamlined platform.",
    mode: "vertical",
  },
  {
    image: ADVANTAGE_IMG_MAP?.speed,
    title: "Speed up turnaround times",
    text: "Avoid delays caused by missing information & endless back & forth emails.",
    mode: "horizontal",
  },
  {
    image: ADVANTAGE_IMG_MAP?.reducing,
    title: "Reduce operational costs",
    text: "Handle more applications without increasing workload or headcount.",
    mode: "horizontal",
  },
]

const { Title, Paragraph } = Typography

const ProblemSolutionSection: FC = () => {
  const { xxl, xl, lg, md, sm } = useBreakpoint()

  return (
    <div className={"problem-solution-section-wrapper"}>
      <div className={"container"}>
        {/*==>> Negative part*/}

        <Row gutter={24} style={{ paddingBottom: 100 }}>
          <Col xxl={12} xl={12} lg={13} md={24} sm={24} xs={24}>
            <Title
              level={!(xxl || xl || lg || md || sm) ? 3 : 2}
              color={"white"}
              style={{ paddingBottom: 16 }}
              centered={!(xxl || xl || lg)}
            >
              <span className={"w300"}>{"Your wayleave process "}</span>
              <span className={"orange-title"}>{"should not "}</span>
              <span className={"w300"}>{"be this time consuming."}</span>
            </Title>
            <Paragraph
              lh={"lh-xl"}
              color={"website-secondary"}
              size={"lg"}
              style={{ paddingBottom: !(xxl || xl || lg) ? 48 : 24 }}
              centered={!(xxl || xl || lg)}
            >
              {
                "Managing wayleave applications often means dealing with scattered emails, missing documents, unclear requirements, and constant follow-ups."
              }
            </Paragraph>

            <div className={"hex-list-container"}>
              {hexListItems.map((it, index) => (
                <HexListItem key={index} icon={it?.icon} text={it?.text} />
              ))}
            </div>
          </Col>

          <Col span={12} xxl={12} xl={12} lg={11} md={24} sm={24} xs={24}>
            <Row justify={"center"}>
              <Col xxl={22} xl={22} lg={24} md={16} sm={18} xs={23}>
                <img src={beforeImg} alt={""} className={"before-img"} loading={"lazy"} decoding={"async"} />
              </Col>
            </Row>
          </Col>
        </Row>

        <Col span={23}>
          <Row justify={"center"}>
            <div className={"logo-connector-wrapper"}>
              <img src={wideLogo} alt={""} loading={"lazy"} decoding={"async"} />

              <div className={"connector-wrapper"}>
                <img
                  src={!(xxl || xl || lg) ? straightConnector : connector}
                  alt={""}
                  loading={"lazy"}
                  decoding={"async"}
                />
              </div>
            </div>
          </Row>
        </Col>

        {/*==>> Positive Solutions*/}

        <Row gutter={24} style={{ paddingBottom: 64 }}>
          {!(xxl || xl || lg) && (
            <Col xxl={12} xl={12} lg={13} md={24} sm={24} xs={24} style={{ paddingTop: 16 }}>
              <Title level={!md ? 3 : 2} color={"white"} style={{ paddingBottom: 16 }} centered={!(xxl || xl || lg)}>
                <span className={"w300"}>{"There is a "}</span>
                <span className={"orange-title"}>{"faster,\nsimpler "}</span>
                <span className={"w300"}>{"way to\nmanage wayleave\napplications."}</span>
              </Title>
            </Col>
          )}

          <Col xxl={11} xl={12} lg={11} md={24} sm={24} xs={24}>
            <Row justify={!(xxl || xl || lg) ? "center" : "start"}>
              <Col xxl={18} xl={18} lg={18} md={16} sm={18} xs={23}>
                <img src={afterImg} alt={""} className={"after-img"} loading={"lazy"} decoding={"async"} />
              </Col>
            </Row>
          </Col>

          <Col xxl={12} xl={12} lg={13} md={24} sm={24} xs={24} style={{ paddingTop: 16 }}>
            {(xxl || xl || lg) && (
              <Title level={2} color={"white"} style={{ paddingBottom: 16 }} centered={!(xxl || xl || lg)}>
                <span className={"w300"}>{"There is a "}</span>
                <span className={"orange-title"}>{"faster,\nsimpler "}</span>
                <span className={"w300"}>{"way to\nmanage wayleave\napplications."}</span>
              </Title>
            )}

            <Col xxl={20} xl={20} lg={20} md={24} sm={24} xs={24} style={{ paddingLeft: 0 }}>
              <Paragraph
                lh={"lh-xl"}
                color={"website-secondary"}
                size={"lg"}
                style={{ paddingBottom: 24 }}
                centered={!(xxl || xl || lg)}
              >
                <span>{"Easetech replaces "}</span>
                <span className={"bold-white"}>{"slow, manual processes "}</span>
                <span>{"with a streamlined system designed "}</span>
                <span className={"bold-white"}>{"specifically "}</span>
                <span>
                  {"for wayleave processes to keep applications on track & processed with industry leading speed."}
                </span>
              </Paragraph>
            </Col>
          </Col>
        </Row>

        {/*  Positive Cards*/}

        <Row justify={"center"} gutter={[24, 24]} style={{ paddingBottom: 24 }}>
          {advantageCardContent &&
            advantageCardContent?.slice(0, 3)?.map((it, index, arr) => (
              <Col xxl={8} xl={8} lg={8} md={8} sm={24} xs={24} key={index}>
                <AdvantageCard
                  image={it?.image}
                  title={it?.title}
                  text={it?.text}
                  mode={sm && !md ? "horizontal" : "vertical"}
                  isLast={index === arr.length - 1}
                />
              </Col>
            ))}
        </Row>

        <Row justify={"center"} gutter={[24, 24]} style={{ paddingBottom: 32 }}>
          {advantageCardContent &&
            advantageCardContent?.slice(3, 5)?.map((it, index, arr) => (
              <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24} key={index}>
                <AdvantageCard
                  image={it?.image}
                  title={it?.title}
                  text={it?.text}
                  mode={xxl || xl || lg || md || sm ? "horizontal" : "vertical"}
                />
              </Col>
            ))}
        </Row>

        <Paragraph color={"website-secondary"} size={"lg"} centered>
          {"Less admin. Faster approvals. Lower costs."}
        </Paragraph>
      </div>
    </div>
  )
}

export default ProblemSolutionSection
